import { Component, OnInit, Inject, ViewChild } from "@angular/core";
import { VngDialogRef, VNG_DIALOG_DATA, VngSelectOption } from "@vengreso/angular-components";
import { FormControl } from "@angular/forms";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { CategoryRenameComponent } from "../add-shortcut-category/category-rename/category-rename.component";
import { CategorySelectComponent } from "../../shared/category-select/category-select.component";
import { NotificationService } from "../../../core/core-services/services/notification.service";
import { distinctUntilChanged } from "rxjs/operators";
import {
  ShortcutCategoryService,
  SHORTCUT_CATEGORIES,
} from "src/app/core/core-services/services/shortcut-category.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { DynamicStorage } from "src/app/core/core-services/decorators/dynamic-storage";

@Component({
    selector: "app-change-shortcut-category",
    templateUrl: "./change-shortcut-category.component.html",
    styleUrls: ["./change-shortcut-category.component.scss"],
    standalone: false
})
export class ChangeShortcutCategoryComponent implements OnInit {
  @DynamicStorage(SHORTCUT_CATEGORIES) shortcutCategoryList: any[];
  // R5: mat-select + the old select-search plugin -> vng-select [searchable]="true".
  categoryOptions: VngSelectOption[] = [];
  category = new FormControl();
  createdCategoryId: string;
  selected: string;
  @ViewChild("categorySelect") categorySelect: CategorySelectComponent;
  category_level = 0;
  constructor(
    public dialogRef: VngDialogRef<ChangeShortcutCategoryComponent>,
    public dialog: DialogService,
    private readonly notification: NotificationService,
    @Inject(VNG_DIALOG_DATA) public data: any,
    private readonly dataService: DataService,
    private readonly sharedData: SharedDataService,
    private readonly shortcutCategoryService: ShortcutCategoryService
  ) {}

  ngOnInit(): void {
    this.getShortcutCategoryList();

    this.sharedData.storage
      .observe(SHORTCUT_CATEGORIES)
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
      .subscribe((value) => {
        this.shortcutCategoryList = value;
        this.buildCategoryOptions();
        this.setSelectedCategory();
      });

    this.buildCategoryOptions();
    this.setSelectedCategory();
  }

  // R5: flatten the category tree into data-driven vng-select options once
  // per shortcutCategoryList change. Each option's `data.level` replaces the
  // old per-mat-option (click)="setCategoryLevel(N)" side effect — read back
  // in onCategorySelected() below.
  buildCategoryOptions(): void {
    const options: VngSelectOption[] = [];
    for (const cat of this.shortcutCategoryList || []) {
      options.push({ value: cat.id, label: cat.name, data: { level: 0 } });
      for (const sub1 of cat.sub_category_lv1 || []) {
        options.push({ value: sub1.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0" + sub1.name, data: { level: 1 } });
        for (const sub2 of sub1.sub_category_lv2 || []) {
          options.push({ value: sub2.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0" + sub2.name, data: { level: 2 } });
        }
      }
    }
    this.categoryOptions = options;
  }

  onCategorySelected(value: any): void {
    const opt = this.categoryOptions.find((o) => o.value == value);
    this.setCategoryLevel(opt?.data?.level ?? 0);
  }

  saveCategory(data) {
    this.dataService.post({ url: "v1/user/shortcut-category", data }).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.createdCategoryId = response.result;
          this.getShortcutCategoryList(true);
          this.categorySelect.closePanel();
          this.notification.toast("Category added successfully!");
        }
      },
      () => {}
    );
  }

  setSelectedCategory() {
    let selectedCat = this.createdCategoryId || this.data.data.category_id || this.shortcutCategoryList[0].id;
    this.setCategoryLevel(0);
    let set = false;

    if (
      (this.data?.data?.sub_categories_lv2_name && this.data?.data?.sub_categories_lv2_name != "") ||
      this.data?.data?.sub_categories_lv2_id
    ) {
      this.selected = this.data.data.sub_categories_lv2_id;
      this.category.setValue(this.data.data.sub_categories_lv2_id);
      this.setCategoryLevel(2);
      set = true;
    }

    if (
      !set &&
      ((this.data?.data?.sub_categories_lv1_name && this.data?.data?.sub_categories_lv1_name != "") ||
        this.data?.data?.sub_categories_lv1_id)
    ) {
      this.selected = this.data.data.sub_categories_lv1_id;
      this.category.setValue(this.data.data.sub_categories_lv1_id);
      this.setCategoryLevel(1);
      set = true;
    }

    if (!set || !this.selected) {
      this.selected = selectedCat;
      this.category.setValue(selectedCat);
    }
  }

  shortcutCategoryRename(data: any) {
    this.dialog.openDialogComponent(CategoryRenameComponent, {
      self: this,
      data,
      backdropClass: "dialog-category-backdrop",
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  setCategoryLevel(level): void {
    this.category_level = level;
  }

  onSaveCLick(): void {
    const shortcut: any = this.dataService.toSave(this.data.data, ["title", "shortcut", "html", "category_id"]);

    shortcut.category_id = this.category.value;
    shortcut.category_level = this.getCategoryLV(this.category.value);
    this.data.self.shortcutUpdate(this.data.data, shortcut, true);
    this.getShortcutCategoryList(true);
    this.dialogRef.close();
  }

  // Derives the level from the same source of truth as onCategorySelected():
  // categoryOptions[].data.level, built by buildCategoryOptions() from the
  // display-nested sub_category_lv1/sub_category_lv2 arrays. Rebuilt here
  // (rather than trusting a possibly-stale this.categoryOptions) so this is
  // correct regardless of call order relative to ngOnInit/onCategorySelected.
  getCategoryLV(id) {
    this.buildCategoryOptions();
    const opt = this.categoryOptions.find((o) => o.value == id);
    return opt?.data?.level ?? 0;
  }

  getShortcutCategoryList(reloadFromApi = false) {
    this.shortcutCategoryService.loadShortcutCategory({ reloadFromApi });
  }
}
